/**
 * DataList bulk actions bar.
 *
 * Renders a floating bar when rows are selected, showing the selection count
 * and configured bulk action buttons. Actions with a `confirm` property
 * display a confirmation modal before executing.
 *
 * Visible only when `selectedRows.length > 0` and at least one bulk action
 * is configured.
 */

import { useState } from 'react';
import { Button } from '../../../base/actions/Button/Button';
import { Modal } from '../../../base/feedback/Modal/Modal';
import { Text } from '../../../base/display/Text/Text';
import { useDataListContext } from '../context/DataListContext';
import type { DataListBulkActionsProps, BulkActionConfig } from '../types';

export function DataListBulkActions({ className }: Readonly<DataListBulkActionsProps>) {
  const { config, list } = useDataListContext();
  const bulkActions = config.actions?.bulkActions;
  const selectedCount = list.selectedRows.length;

  const [confirmAction, setConfirmAction] = useState<BulkActionConfig | null>(null);

  if (selectedCount === 0 || !bulkActions || bulkActions.length === 0) {
    return null;
  }

  const handleAction = async (action: BulkActionConfig) => {
    if (action.confirm) {
      setConfirmAction(action);
      return;
    }
    await list.executeBulkAction(action.id);
  };

  const handleConfirm = async () => {
    /* v8 ignore start */
    if (confirmAction) {
      /* v8 ignore stop */
      await list.executeBulkAction(confirmAction.id);
      setConfirmAction(null);
    }
  };

  const combinedClasses = [
    'flex items-center gap-3 rounded-lg bg-blue-50 px-4 py-2',
    config.cssOverrides?.bulkActions,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div className={combinedClasses}>
        <Text size="sm" weight="medium">
          {selectedCount} {selectedCount === 1 ? 'item' : 'items'} selected
        </Text>

        {bulkActions.map((action) => (
          <Button
            key={action.id}
            variant={action.variant ?? 'secondary'}
            size="sm"
            onClick={() => handleAction(action)}
          >
            {action.label}
          </Button>
        ))}
      </div>

      {confirmAction?.confirm && (
        <Modal
          open={Boolean(confirmAction)}
          onClose={() => setConfirmAction(null)}
          size="sm"
        >
          <Modal.Header title={confirmAction.confirm.title} />
          <Modal.Body>
            <Text>{confirmAction.confirm.message}</Text>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="ghost" onClick={() => setConfirmAction(null)}>
              {confirmAction.confirm.cancelLabel ?? 'Cancel'}
            </Button>
            <Button
              variant={confirmAction.confirm.variant ?? 'primary'}
              onClick={handleConfirm}
            >
              {confirmAction.confirm.confirmLabel ?? 'Confirm'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}

DataListBulkActions.displayName = 'DataListBulkActions';
